import type { Deal, Vertical } from '../types';
import { isWonStage } from './orders';

export interface StageBucket { stage: string; count: number }
const configured = (vertical: Vertical | undefined, key: string): string[] => {
  const value = vertical?.settings?.[key];
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
};

// Count deals per stage in the pipelines' configured order; unknown stages
// (legacy or unreviewed labels) follow in first-seen order so nothing is hidden.
export function stageBuckets(deals: Deal[], verticals: Vertical[]): StageBucket[] {
  const order: string[] = [];
  for (const vertical of verticals) for (const stage of configured(vertical, 'deal_stages')) if (!order.includes(stage)) order.push(stage);
  const counts = new Map<string, number>();
  for (const deal of deals) counts.set(deal.stage, (counts.get(deal.stage) ?? 0) + 1);
  const known = order.filter(stage => counts.has(stage)).map(stage => ({ stage, count: counts.get(stage)! }));
  const extra = [...counts.keys()].filter(stage => !order.includes(stage)).map(stage => ({ stage, count: counts.get(stage)! }));
  return [...known, ...extra];
}

// Reporting-terminal deals split into won (may still need fulfilment and a
// happy call) and lost, using each deal's own pipeline won-stage settings.
export function splitClosedDeals(deals: Deal[], verticals: Vertical[]): { won: Deal[]; lost: Deal[] } {
  const byId = new Map(verticals.map(vertical => [vertical.id, vertical]));
  const won: Deal[] = [], lost: Deal[] = [];
  for (const deal of deals) (isWonStage(deal.stage, configured(byId.get(deal.vertical_id), 'won_stages')) ? won : lost).push(deal);
  return { won, lost };
}

// Open deals that have gone longest without an update, for the "needs a nudge" list.
export function dealsNeedingNudge(deals: Deal[], limit = 3): Deal[] {
  return [...deals].sort((a, b) => Date.parse(a.updated_at) - Date.parse(b.updated_at) || a.id - b.id).slice(0, limit);
}

export const relationshipLabels: Record<string, string> = {
  architect: 'Architect', interior_designer: 'Interior designer', lighting_vendor: 'Lighting vendor', lighting_consultant: 'Lighting consultant',
  pmc: 'PMC', electrical_contractor: 'Electrical contractor', homeowner: 'Homeowner', other: 'Business contact', '': 'Business contact'
};
export function initials(first: string | null | undefined, last?: string | null): string {
  const letters = `${(first ?? '').trim().charAt(0)}${(last ?? '').trim().charAt(0)}`.toUpperCase();
  return letters || '·';
}
